import { Footer } from '@components/Footer';
import { Header } from '@components/Header';

import { COLORS } from '@constants/colors';
import { CONTACT_INFO } from '@constants/contact';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 flex flex-col">
      <Header showNavigation={false} showBackButton={true} />

      {/* Main Content */}
      <main className="flex-1 py-12 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-8 lg:p-12">
            {/* Header */}
            <div className="text-center mb-8 sm:mb-12">
              <h1
                className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
                style={{ color: COLORS.primary }}
              >
                Privacy policy
              </h1>
              <p
                className="text-base sm:text-lg"
                style={{ color: COLORS.smoke }}
              >
                Last updated: September 28, 2025
              </p>
            </div>

            {/* Content */}
            <div className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2
                  className="text-xl sm:text-2xl font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  1. Information we collect
                </h2>
                <div
                  className="space-y-4 text-sm sm:text-base"
                  style={{ color: COLORS.charcoal }}
                >
                  <p>
                    <strong>Personal information:</strong> When you contact us
                    or use our services, we may collect personal information
                    such as:
                  </p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Name, email address, and phone number</li>
                    <li>Financial goals and investment preferences</li>
                    <li>KYC documents as required by regulatory authorities</li>
                    <li>Investment history and portfolio information</li>
                  </ul>
                  <p>
                    <strong>Usage information:</strong> We automatically collect
                    information about how you use our website, including IP
                    address, browser type, and pages visited.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2
                  className="text-xl sm:text-2xl font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  2. How we use your information
                </h2>
                <div
                  className="space-y-4 text-sm sm:text-base"
                  style={{ color: COLORS.charcoal }}
                >
                  <p>We use your information to:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      Provide personalized investment advice and recommendations
                    </li>
                    <li>
                      Process your mutual fund investments and transactions
                    </li>
                    <li>
                      Comply with regulatory requirements (KYC, AML, etc.)
                    </li>
                    <li>
                      Communicate with you about your investments and market
                      updates
                    </li>
                    <li>Improve our services and website functionality</li>
                    <li>
                      Send periodic newsletters and educational content (with
                      your consent)
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2
                  className="text-xl sm:text-2xl font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  3. Information Sharing and Disclosure
                </h2>
                <div
                  className="space-y-4 text-sm sm:text-base"
                  style={{ color: COLORS.charcoal }}
                >
                  <p>We may share your information with:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>
                      <strong>Mutual fund companies:</strong> To process your
                      investments and maintain your folios
                    </li>
                    <li>
                      <strong>Regulatory authorities:</strong> As required by
                      SEBI, AMFI, and other regulatory bodies
                    </li>
                    <li>
                      <strong>Service providers:</strong> Third-party services
                      that help us operate our business (with appropriate data
                      protection agreements)
                    </li>
                    <li>
                      <strong>Legal requirements:</strong> When required by law
                      or to protect our rights
                    </li>
                  </ul>
                  <p>
                    <strong>
                      We do not sell, rent, or trade your personal information
                      to third parties for marketing purposes.
                    </strong>
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2
                  className="text-xl sm:text-2xl font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  4. Data security
                </h2>
                <div
                  className="space-y-4 text-sm sm:text-base"
                  style={{ color: COLORS.charcoal }}
                >
                  <p>
                    We implement appropriate technical and organizational
                    measures to protect your personal information against
                    unauthorized access, alteration, disclosure, or destruction.
                    This includes:
                  </p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>SSL encryption for data transmission</li>
                    <li>Secure servers and databases</li>
                    <li>Regular security audits and updates</li>
                    <li>
                      Limited access to personal information on a need-to-know
                      basis
                    </li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2
                  className="text-xl sm:text-2xl font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  5. Your rights
                </h2>
                <div
                  className="space-y-4 text-sm sm:text-base"
                  style={{ color: COLORS.charcoal }}
                >
                  <p>You have the right to:</p>
                  <ul className="list-disc ml-6 space-y-2">
                    <li>Access and review your personal information</li>
                    <li>Request corrections to inaccurate information</li>
                    <li>
                      Request deletion of your information (subject to
                      regulatory requirements)
                    </li>
                    <li>Opt-out of marketing communications</li>
                    <li>File a complaint with relevant authorities</li>
                  </ul>
                </div>
              </section>

              <section className="mb-8">
                <h2
                  className="text-xl sm:text-2xl font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  6. Cookies and tracking
                </h2>
                <div
                  className="space-y-4 text-sm sm:text-base"
                  style={{ color: COLORS.charcoal }}
                >
                  <p>
                    Our website uses cookies to enhance your browsing experience
                    and analyze website traffic. You can control cookie settings
                    through your browser preferences.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2
                  className="text-xl sm:text-2xl font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  7. Changes to this policy
                </h2>
                <div
                  className="space-y-4 text-sm sm:text-base"
                  style={{ color: COLORS.charcoal }}
                >
                  <p>
                    We may update this Privacy Policy from time to time. We will
                    notify you of any significant changes by posting the new
                    policy on our website and updating the "Last updated" date.
                  </p>
                </div>
              </section>

              <section className="mb-8">
                <h2
                  className="text-xl sm:text-2xl font-semibold mb-4"
                  style={{ color: COLORS.primary }}
                >
                  8. Contact us
                </h2>
                <div
                  className="space-y-4 text-sm sm:text-base"
                  style={{ color: COLORS.charcoal }}
                >
                  <p>
                    If you have any questions about this privacy policy or our
                    data practices, please contact us:
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p>
                      <strong>Email:</strong> {CONTACT_INFO.email.privacy}
                    </p>
                    <p>
                      <strong>Phone:</strong> {CONTACT_INFO.phone.display}
                    </p>
                    <p>
                      <strong>Address:</strong> {CONTACT_INFO.address.full}
                    </p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
